import type { FC } from 'react';
import { useState, useContext, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 68 (studio.batchActions.title.${batchAction}; batchAction from BatchAction):
//   studio.batchActions.title.edit_title
//   studio.batchActions.title.edit_tags
//   studio.batchActions.title.edit_description
//   studio.batchActions.title.edit_gallery
//   studio.batchActions.title.edit_tier
//   studio.batchActions.title.edit_schedule
//   studio.batchActions.title.edit_license
//   studio.batchActions.title.edit_display_options
//   studio.batchActions.title.edit_comments
//   studio.batchActions.title.edit_labels
//   studio.batchActions.title.edit_preset
//   studio.batchActions.title.bool_ai_add
//   studio.batchActions.title.bool_ai_del
//   studio.batchActions.title.bool_noai_add
//   studio.batchActions.title.bool_noai_del
//   studio.batchActions.title.bool_mature_add
//   studio.batchActions.title.bool_mature_del
//   studio.batchActions.title.other_delete
//   studio.batchActions.title.other_unpublish
//   studio.batchActions.title.other_move_to_folder
//   studio.batchActions.title.other_create_folder_from_selection
//   studio.batchActions.title.other_merge
//   studio.batchActions.title.other_move_to_stash
//   studio.batchActions.title.other_move_to_drafts
//   studio.batchActions.title.other_sell
import { Url } from '@wix/da-url';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import BatchEditOrDeleteConfirmation from '../../../modals/BatchEditOrDeleteConfirmation';
import { BatchAction } from '../../../types/store';
import { BatchActionProps } from '../types';

import s from './DesktopBatchActionWrapper.scss';

export interface Props extends BatchActionProps {
  batchAction: BatchAction;
  selectedCount: number;
  onCancel: () => void;
  onSave: () => void;
  coreUpgradeBiData?: ReturnType<typeof BiData<BuyCoreButtonBiEvent>>;
  // actions can be restricted to Core users only, or non Core who can edit one or multiple items depending on each case
  restrictSave?: 'core' | 'non_core_single' | 'non_core_multiple';
}

const DesktopBatchActionWrapper: FC<PropsWithChildren<Props>> = ({
  heading,
  selectionArea,
  batchAction,
  selectedCount,
  children,
  disableSave,
  onCancel,
  onSave,
  coreUpgradeBiData,
  restrictSave = 'non_core_single',
}) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const [isShowingConfirmation, setShowingConfirmation] = useState(false);
  const canSave =
    hasCoreMembership ||
    restrictSave === 'non_core_multiple' ||
    (restrictSave === 'non_core_single' && selectedCount < 2);

  const biData =
    coreUpgradeBiData ||
    BiData<BuyCoreButtonBiEvent>({
      evid: 375,
      referral_info: ReferralType.STUDIO_BATCH_ACTION,
    });

  return (
    <ThemeOverride themeSurface={ThemeSurface.TERTIARY} tag="div" className={s['root']}>
      <div className={s['select']}>{selectionArea}</div>
      <div className={s['title']}>{t(`studio.batchActions.title.${batchAction}`)}</div>
      <div className={s['cta']}>
        <TextButton variant="main" size="medium" onClick={onCancel}>
          {t('studio.batchActions.cta.cancel')}
        </TextButton>
        {canSave ? (
          <BrandedButton
            variant="brand-green"
            size="medium"
            onClick={() => setShowingConfirmation(true)}
            disabled={disableSave}
          >
            {t('studio.batchActions.cta.save')}
          </BrandedButton>
        ) : (
          <SimpleTooltip
            text={t('studio.batchActions.cta.saveNonCore.tooltip', {
              postProcess: 'reactPostprocessor',
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['tooltip-upgrade-link']}
                  href={Url.coreMembershipLink()}
                  target="_blank"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.STUDIO_MULTI_SUBMIT_TOOLTIP,
                  })}
                >
                  {t('studio.table.header.submitNonCore.upgradeLink')}
                </PlainButtonOrLink>
              ),
            })}
            usePortal
          >
            <BuyCoreBrandedButton size="medium" biData={biData}>
              {t('studio.batchActions.cta.saveNonCore')}
            </BuyCoreBrandedButton>
          </SimpleTooltip>
        )}
      </div>
      <div className={s['separator']} />
      <div className={s['form']}>
        {heading && <div className={s['heading']}>{heading}</div>}
        {children}
      </div>

      <BatchEditOrDeleteConfirmation
        isOpen={isShowingConfirmation}
        selectedCount={selectedCount}
        onConfirm={onSave}
        onCancel={() => setShowingConfirmation(false)}
      />
    </ThemeOverride>
  );
};
DesktopBatchActionWrapper.displayName = 'DesktopBatchActionWrapper';

export default DesktopBatchActionWrapper;
